import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import React, { useRef } from "react";
import { Box } from "@material-ui/core";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend
);

const DOT = "#23b578";
const STANDARD = "#ff0000";
const AREA = "rgba(35,181,120,0.15)";
const SUCCESS_BG = "#e6f4ea";
const ERROR_BG = "#fbeaea";

const annotatedLinePlugin = {
  id: "annotatedVerticalLine",
  afterDraw(chart) {
    if (chart.tooltip?._active?.length) {
      const x = chart.tooltip._active[0].element.x;
      const yAxis = chart.scales.y;
      const ctx = chart.ctx;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.lineWidth = 9;
      ctx.strokeStyle = "rgba(0, 0, 255, 0.2)";
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(0, 0, 255, 0.4)";
      ctx.stroke();
      ctx.restore();
    }
  },
};

const referenceAreaPlugin = {
  id: "referenceArea",
  beforeDatasetsDraw(chart) {
    const {
      ctx,
      chartArea: { bottom },
      scales: { x, y },
    } = chart;

    ctx.save();
    ctx.fillStyle = AREA;

    chart.config.data.datasets[0].data.forEach((d) => {
      const year = new Date(d.x).getFullYear();
      const startX = x.getPixelForValue(new Date(year - 1, 6, 1));
      const endX = x.getPixelForValue(new Date(year, 6, 1));
      const yVal = y.getPixelForValue(d.y);

      ctx.beginPath();
      ctx.moveTo(startX, bottom);
      ctx.lineTo(startX, yVal);
      ctx.lineTo(endX, yVal);
      ctx.lineTo(endX, bottom);
      ctx.closePath();
      ctx.fill();
    });

    ctx.restore();
  },
};

export default function SeasonalMeanChart({ raw, series, unit, height }) {
  const chartRef = useRef();

  function getStandardForYear(year) {
    const row = raw.find((r) => new Date(r.date_time).getFullYear() === year);
    return row?.chlora_standard ?? Infinity;
  }

  const data = {
    datasets: [
      {
        label: "Mean",
        data: series.map((d) => ({ x: d.date, y: d.value })),
        backgroundColor: DOT,
        pointRadius: 5,
        showLine: false,
        order: 1,
      },
      {
        label: "Standard",
        type: "line",
        data: raw.map((d) => ({
          x: new Date(d.date_time),
          y: d.chlora_standard ?? null,
        })),
        borderColor: STANDARD,
        borderWidth: 2,
        fill: false,
        spanGaps: false,
        pointRadius: 0,
        order: 2,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      axis: "x",
      intersect: false,
    },
    plugins: {
      legend: {
        labels: {
          filter: (item) => item.text !== "",
        },
      },
      tooltip: {
        mode: "index",
        axis: "x",
        intersect: false,
        padding: 12,
        borderWidth: 2,
        borderColor: (ctx) => {
          const dp = ctx.tooltip.dataPoints?.find(
            (d) => d.dataset.label === "Mean"
          );
          if (!dp) return "#ccc";
          const year = new Date(dp.raw.x).getFullYear();
          const standard = getStandardForYear(year);
          if (!isFinite(standard)) return "#ccc";
          return dp.raw.y < standard ? DOT : STANDARD;
        },
        backgroundColor: (ctx) => {
          const dp = ctx.tooltip.dataPoints?.find(
            (d) => d.dataset.label === "Mean"
          );
          if (!dp) return "#fff";
          const year = new Date(dp.raw.x).getFullYear();
          const standard = getStandardForYear(year);
          if (!isFinite(standard)) return "#fff";
          return dp.raw.y < standard ? SUCCESS_BG : ERROR_BG;
        },
        titleFont: { weight: "bold", size: 14 },
        bodyFont: { size: 14 },
        titleColor: "#333",
        bodyColor: "#333",
        cornerRadius: 6,
        callbacks: {
          title: (tooltipItems) => {
            const date = tooltipItems[0]?.raw?.x;
            return new Date(date).getFullYear();
          },
          label: (ti) => {
            const year = new Date(ti.raw.x).getFullYear();
            const value = ti.raw.y.toFixed(2);
            if (ti.dataset.label === "Mean") {
              return `Mean: ${value} ${unit}`;
            }
            const standard = getStandardForYear(year);
            if (standard === Infinity || standard == null) return null;
            return `Standard: ${standard} ${unit}`;
          },
          labelColor: (ti) => {
            const color = ti.dataset.label === "Mean" ? DOT : STANDARD;
            return {
              borderColor: color,
              backgroundColor: color,
            };
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "year" },
      },
      y: {
        title: { display: true, text: `Concentration (${unit})` },
      },
    },
  };

  return (
    <Box height={height} width="100%">
      <Scatter
        ref={chartRef}
        data={data}
        options={options}
        plugins={[referenceAreaPlugin, annotatedLinePlugin]}
      />
    </Box>
  );
}
